import React, { useState } from 'react';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { supabase } from '@/utils/supabase';
import { analytics } from '@/utils/analytics';

interface ForgotPasswordFormProps {
  onSwitchToLogin?: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onSwitchToLogin
}) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError('Veuillez saisir votre adresse email');
      return;
    }

    // Track form submission
    analytics.trackFormStart('forgot_password');
    
    setLoading(true);
    setError(null);

    try {
const { error } = await supabase.auth.resetPasswordForEmail(email, {
  redirectTo: 'https://www.dpewizard.com/auth?mode=update-password', // URL fixe avec www
});

      if (error) {
        throw error;
      }

      setSuccess(true);
      
      // Track successful password reset request
      analytics.trackEvent({
        action: 'submit',
        category: 'form',
        label: 'forgot_password_success'
      });
      
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.message || 'Erreur lors de l\'envoi de l\'email de réinitialisation');
      
      // Track password reset error
      analytics.trackError(err, 'forgot_password_form');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Email envoyé !</h2>
          <p className="text-gray-600 mb-6">
            Un email de réinitialisation a été envoyé à <strong>{email}</strong>. 
            Vérifiez votre boîte de réception et cliquez sur le lien pour définir un nouveau mot de passe.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Vous ne recevez pas l'email ? Vérifiez vos spams ou réessayez avec une autre adresse.
          </p>
          <Button
            variant="outline"
            onClick={onSwitchToLogin}
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Retour à la connexion
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-6 h-6 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Mot de passe oublié</h1>
          <p className="text-gray-600">
            Saisissez votre adresse email pour recevoir un lien de réinitialisation
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="votre@email.com"
            label="Adresse email"
            leftIcon={<Mail className="w-5 h-5" />}
            required
            disabled={loading}
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={loading}
            className="w-full"
          >
            Envoyer le lien de réinitialisation
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Vous vous souvenez de votre mot de passe ?{' '}
            <button
              onClick={onSwitchToLogin}
              className="text-blue-600 hover:text-blue-700 font-medium"
              disabled={loading}
            >
              Retour à la connexion
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;